/**
 * / — role-aware landing.
 *
 * Platform admins get the platform overview dashboard (KPIs,
 * alerts, sign-in trend, recent activity, recent tenants), paired
 * with the per-tenant overview at `/tenants/$slug` which uses the
 * same layout skeleton.
 *
 * Tenant-scope admins are redirected to their own tenant console
 * at `/tenants/{slug}` — the slug comes from the session (/me
 * ships tenant_slug for tenant-scope roles). Without the redirect
 * a tenant admin would land on the platform dashboard and 403 on
 * `GET /api/v1/platform/overview` (platform-only endpoint).
 *
 * The session is already in the query cache — the parent
 * `_authed.tsx` beforeLoad ensures it — so beforeLoad here reads
 * it synchronously; the platform-overview prefetch runs only for
 * platform admins.
 */

import { createFileRoute, redirect } from "@tanstack/react-router";
import { fetchPlatformOverview } from "@/features/platform-dashboard/queries";
import { queryKeys } from "@/lib/query-keys";
import { sessionQueryKey, type AdminSession } from "@/queries/session";
import { PlatformDashboardPage } from "@/features/platform-dashboard/pages/platform-dashboard-page";

export const Route = createFileRoute("/_authed/")({
  beforeLoad: ({ context }) => {
    const session = context.queryClient.getQueryData<AdminSession>(sessionQueryKey);
    if (session?.admin.organization_id) {
      throw redirect({ to: "/organization/sign-in" });
    }
    if (session && !session.is_platform_admin && session.admin.tenant_slug) {
      throw redirect({
        to: "/tenants/$slug",
        params: { slug: session.admin.tenant_slug },
      });
    }
  },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.platformOverview,
      queryFn: fetchPlatformOverview,
    });
  },
  component: PlatformDashboardPage,
});
